import {useCallback, useEffect, useRef, useState} from 'react';

const DEBOUNCE_MS = 500;

export const useCommentNavigation = ({instance, NutrientViewer, isReady}) => {
  const [commentAnnotations, setCommentAnnotations] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const debounceTimerRef = useRef(null);
  const currentAnnotationIdRef = useRef(null);

  const collectCommentAnnotations = useCallback(async () => {
    if (!instance || !NutrientViewer) {
      setCommentAnnotations([]);
      setCurrentIndex(-1);
      return;
    }

    try {
      const comments = await instance.getComments();
      if (!comments || comments.size === 0) {
        setCommentAnnotations([]);
        setCurrentIndex(-1);
        currentAnnotationIdRef.current = null;
        return;
      }

      // Extract unique rootIds and group by page
      const rootIdToPage = new Map(); // {rootId: pageIndex}
      comments.forEach((comment) => {
        if (comment.rootId && comment.pageIndex != null) {
          rootIdToPage.set(comment.rootId, comment.pageIndex);
        }
      });

      if (rootIdToPage.size === 0) {
        setCommentAnnotations([]);
        setCurrentIndex(-1);
        currentAnnotationIdRef.current = null;
        return;
      }

      // Group rootIds by page to collect annotations for relevant pages only
      const pageToRootIds = new Map(); // {pageIndex: [rootId1, rootId2, ...]}
      rootIdToPage.forEach((pageIndex, rootId) => {
        if (!pageToRootIds.has(pageIndex)) {
          pageToRootIds.set(pageIndex, new Set());
        }
        pageToRootIds.get(pageIndex).add(rootId);
      });

      // Fetch annotations for relevant pages only
      const collected = [];
      const pagePromises = Array.from(pageToRootIds.entries()).map(
        async ([pageIndex, rootIds]) => {
          try {
            const annotations = await instance.getAnnotations(pageIndex);
            annotations.forEach((annotation) => {
              if (rootIds.has(annotation.id) && annotation.boundingBox) {
                collected.push({
                  annotationId: annotation.id,
                  pageIndex: annotation.pageIndex,
                  boundingBox: annotation.boundingBox,
                });
              }
            });
          } catch (err) {
            console.warn(
              '[useCommentNavigation] Failed to get annotations for page',
              pageIndex,
              err
            );
          }
        }
      );

      await Promise.all(pagePromises);

      // Sort by page, then vertical position
      collected.sort((a, b) => {
        if (a.pageIndex !== b.pageIndex) return a.pageIndex - b.pageIndex;
        return a.boundingBox.top - b.boundingBox.top;
      });

      setCommentAnnotations(collected);

      // Preserve current selection if possible
      const prevId = currentAnnotationIdRef.current;
      if (prevId) {
        const newIdx = collected.findIndex((c) => c.annotationId === prevId);
        setCurrentIndex(newIdx >= 0 ? newIdx : -1);
        if (newIdx < 0) currentAnnotationIdRef.current = null;
      } else {
        setCurrentIndex(-1);
      }
    } catch (err) {
      console.warn('[useCommentNavigation] Failed to collect comments:', err);
    }
  }, [instance, NutrientViewer]);

  const navigateTo = useCallback(
    (index) => {
      if (!instance || commentAnnotations.length === 0) return;

      const clamped =
        ((index % commentAnnotations.length) + commentAnnotations.length) %
        commentAnnotations.length;
      const entry = commentAnnotations[clamped];
      if (!entry) return;

      try {
        instance.jumpToRect(entry.pageIndex, entry.boundingBox);
        instance.setSelectedAnnotations(
          NutrientViewer.Immutable.List([entry.annotationId])
        );
      } catch (err) {
        console.warn(
          '[useCommentNavigation] Failed to navigate to comment:',
          err
        );
      }

      setCurrentIndex(clamped);
      currentAnnotationIdRef.current = entry.annotationId;
    },
    [instance, commentAnnotations, NutrientViewer]
  );

  const goToNext = useCallback(() => {
    navigateTo(currentIndex + 1);
  }, [navigateTo, currentIndex]);

  const goToPrev = useCallback(() => {
    navigateTo(
      currentIndex <= 0 ? commentAnnotations.length - 1 : currentIndex - 1
    );
  }, [navigateTo, currentIndex, commentAnnotations.length]);

  // Initial collection
  useEffect(() => {
    if (isReady && instance) {
      collectCommentAnnotations();
    }
  }, [isReady, instance, collectCommentAnnotations]);

  // listen to annotations and comments create/delete events & refresh the comments collection
  useEffect(() => {
    if (!isReady || !instance) return;

    const debouncedRefresh = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        collectCommentAnnotations();
      }, DEBOUNCE_MS);
    };

    instance.addEventListener(
      NutrientViewer.EventName.ANNOTATIONS_CREATE,
      debouncedRefresh
    );
    instance.addEventListener(
      NutrientViewer.EventName.ANNOTATIONS_DELETE,
      debouncedRefresh
    );
    instance.addEventListener(
      NutrientViewer.EventName.COMMENTS_CREATE,
      debouncedRefresh
    );
    instance.addEventListener(
      NutrientViewer.EventName.COMMENTS_DELETE,
      debouncedRefresh
    );

    return () => {
      try {
        instance.removeEventListener(
          NutrientViewer.EventName.ANNOTATIONS_CREATE,
          debouncedRefresh
        );
        instance.removeEventListener(
          NutrientViewer.EventName.ANNOTATIONS_DELETE,
          debouncedRefresh
        );
        instance.removeEventListener(
          NutrientViewer.EventName.COMMENTS_CREATE,
          debouncedRefresh
        );
        instance.removeEventListener(
          NutrientViewer.EventName.COMMENTS_DELETE,
          debouncedRefresh
        );
      } catch (err) {
        // Instance may already be unloaded
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isReady, instance, collectCommentAnnotations]);

  return {
    currentIndex,
    totalCount: commentAnnotations.length,
    goToNext,
    goToPrev,
    refresh: collectCommentAnnotations,
  };
};

export default useCommentNavigation;
